import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Song } from '../types';
import { usePlayer } from '../contexts/PlayerContext';

const Container = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: white;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 12px;
  }
`;

const RecentList = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  scroll-snap-type: x mandatory;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary-color);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    padding-left: 4px;
    padding-right: 4px;
    
    &::-webkit-scrollbar {
      display: none;
    }
  }
  
  > * {
    scroll-snap-align: start;
  }
`;

const RecentItem = styled.div`
  min-width: 200px;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    min-width: 160px;
    padding: 12px;
    border-radius: 16px;
    
    &:hover {
      transform: none;
    }
    
    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }
  }
`;

const SongName = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlayedAt = styled.div`
  color: var(--text-muted);
  font-size: 0.8rem;
`;

const RecentlyPlayed: React.FC = () => {
  const [recentSongs, setRecentSongs] = useState<(Song & { playedAt: Date })[]>([]);
  const { playSong } = usePlayer();

  useEffect(() => {
    loadRecentSongs();
  }, []);

  const loadRecentSongs = () => {
    const recent = localStorage.getItem('recentlyPlayed');
    if (recent) {
      const parsed = JSON.parse(recent);
      setRecentSongs(parsed.map((item: any) => ({
        ...item,
        playedAt: new Date(item.playedAt)
      })));
    }
  };

  const formatPlayedAt = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `${minutes}m atrás`;
    if (hours < 24) return `${hours}h atrás`;
    return `${days}d atrás`;
  };

  if (recentSongs.length === 0) return null;

  return (
    <Container>
      <Title>🕒 Tocadas Recentemente</Title>
      <RecentList>
        {recentSongs.slice(0, 10).map((song, index) => (
          <RecentItem key={`${song.id}-${index}`} onClick={() => playSong(song, [song])}>
            <SongName>{song.name}</SongName>
            <PlayedAt>{formatPlayedAt(song.playedAt)}</PlayedAt>
          </RecentItem>
        ))}
      </RecentList>
    </Container>
  );
};

export default RecentlyPlayed;